import { clsx } from 'clsx';
import type { ComponentProps } from 'react';

export interface InputFieldProps extends Omit<ComponentProps<'input'>, 'size'> {
  /** Label displayed above the input */
  label?: string;
  /** Error message displayed below the input */
  error?: string;
}

/**
 * InputField - Atom component for text inputs with label and error support
 *
 * Renders a styled text input with optional label above and error message below.
 * Follows the Dorsia design system with Sailec font, 40px height, and focus ring.
 */
export function InputField({ label, error, className, id, ...props }: InputFieldProps) {
  const inputId = id ?? (label ? `input-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={inputId} className="font-sailec text-dorsia-black text-sm font-medium">
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={clsx(
          'font-sailec rounded-lg px-3 text-dorsia-black placeholder:text-[#8d8d8d] h-10 text-base outline-none transition-colors bg-[#f4f4f4] border',
          error
            ? 'border-dorsia-error focus:border-dorsia-error'
            : 'border-transparent focus:border-dorsia-border-focus'
        )}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && inputId ? `${inputId}-error` : undefined}
        {...props}
      />
      {error && (
        <span id={inputId ? `${inputId}-error` : undefined} className="text-dorsia-error text-xs" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
